import React from "react";
import ModalShell from "./ModalShell";
import Button from "../button";
import colors from "../../design/colors";
import fonts from "../../design/typography";

/* ----------------------------------
 * TYPES
 ---------------------------------- */
interface ConfirmButton {
  text: string;
  onClick: () => void;
  buttonColor: string;
  textColor?: string;
  width?: string;
  height?: string;
  fontStyle?: React.CSSProperties;
  buttonHoverColor?: string;
  disabled?: boolean;
}

interface ConfirmModalProps {
  isOpen: boolean;
  message: React.ReactNode;
  buttons: ConfirmButton[];
  width?: string; // 모달 너비
  onClose: () => void; // 외부 클릭
}

/* ----------------------------------
 * COMPONENT
 * 바깥 틀은 ModalShell(배경·애니메이션·Escape·포커스 가두기·화면 크기 제한·겹친 모달 처리).
 * 예전 부모 모달들의 DOM 포함 판정이 깨지지 않게 그 자리에 그린다(inline).
 ---------------------------------- */
const ConfirmModal = ({
  isOpen,
  message,
  buttons,
  onClose,
  width,
}: ConfirmModalProps) => {
  // 열리면 첫 버튼에 포커스(예전 동작 유지: Enter 가 첫 버튼을 누른다). ModalShell 이 상자에 포커스를 준 다음에 옮긴다
  const focusFirstButton = React.useCallback((node: HTMLDivElement | null) => {
    if (!node) return;
    setTimeout(() => node.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus(), 0);
  }, []);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel="확인"
      // 예전 안쪽 여백 20px -> ModalShell 24px. px 너비면 그만큼(4px x2) 넓혀 글자 폭·줄바꿈 위치를 예전과 같게 둔다
      width={/^\d+(\.\d+)?px$/.test(width ?? "350px") ? `calc(${width ?? "350px"} + 8px)` : width}
      showClose={false}
      inline
    >
      <div ref={focusFirstButton}>
        <div
          style={{
            ...fonts.body2Medium,
            color: colors.gray900,
            textAlign: "center",
            margin: "8px 0 20px",
            overflowWrap: "anywhere",
            whiteSpace: "pre-line",
          }}
        >
          {message}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 8 }}>
          {buttons.map((btn, index) => (
            <Button
              key={index}
              size="medium"
              width={btn.width ?? "112px"}
              height={btn.height ?? "40px"}
              text={btn.text}
              onClick={btn.onClick}
              disabled={btn.disabled}
              buttonColor={btn.buttonColor}
              textColor={btn.textColor}
              fontStyle={btn.fontStyle}
              buttonHoverColor={btn.buttonHoverColor}
            />
          ))}
        </div>
      </div>
    </ModalShell>
  );
};

export default ConfirmModal;
